import { useState } from 'react';

import { TableColumnsType } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useSWR from 'swr';

import IconDown from '@/assets/icons/icon-down.svg?react';
import IconRight from '@/assets/icons/icon-right.svg?react';
import Table from '@/components/RTable';
import useCurrency from '@/contexts/useCurrency';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { converToMillion } from '@/utils';

import StyledProfit from './styles';

interface DataType {
  key: string;
  name: string | JSX.Element;
  total: string;
  [key: string]: string | number | JSX.Element | undefined;
}

const PNLWithoutStorageTable = () => {
  const { currencySymbol } = useCurrency();
  const resultId = useParams<Record<string, string>>();
  const { data: financialData } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId.resultId}/yearly_financial_analysis/`
    );

  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId.resultId}/`
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onExpand = (expanded: boolean, record: any) => {
    const keys = expanded
      ? [...expandedRowKeys, record.key]
      : expandedRowKeys.filter((key) => key !== record.key);
    setExpandedRowKeys(keys);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customExpandIcon = (props: any) => {
    if (!props.record.children) {
      return <span style={{ marginRight: '22px' }} />;
    }
    if (props.expanded) {
      return (
        <IconDown
          width={12}
          height={12}
          style={{ marginRight: '8px' }}
          onClick={(e) => props.onExpand(props.record, e)}
        />
      );
    } else {
      return (
        <IconRight
          width={12}
          height={12}
          style={{ marginRight: '8px' }}
          onClick={(e) => props.onExpand(props.record, e)}
        />
      );
    }
  };

  let years: number[] = [];

  financialData?.graphData.forEach((item) => {
    years.push(item.year);
  });

  const scrollX = 775 + years.length * 100;

  years = [...new Set(years)].sort((a, b) => a - b);

  if (!financialData || !simulationData) {
    return null;
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 400,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 100,
    },
    ...years.map((year) => ({
      title: year.toString(),
      dataIndex: year.toString(),
      key: year.toString(),
      width: 100,
    })),
  ];

  const getValuesForYears = (
    graphData: SimulationResultYearlyFinancialAnalysisData[],
    key: string
  ) => {
    const values: Record<string, string | number> = {};
    if (graphData) {
      years.forEach((year) => {
        const data = graphData.find((item) => item.year === year);
        values[year.toString()] = data
          ? converToMillion(data[key]) + ` ${currencySymbol}`
          : '0';
      });
    }
    return values;
  };

  financialData.graphData.forEach((item) => {
    const totalDepreciation = item.storageDepreciation + item.plantDepreciation;
    item.totalDepreciation = totalDepreciation;
  });

  const dataSource = [
    {
      key: '1',
      name: t('result.finance.profitAndLoss.tables.rows.revenues'),
      total:
        converToMillion(simulationData?.result.revenueWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'revenueWos'),
    },
    // {
    //   key: '14',
    //   name: t('result.finance.profitAndLoss.tables.rows.residualValues'),
    //   total:
    //     converToMillion(simulationData?.result.residualValueOfPlant) + ` ${currencySymbol}`,
    //   ...getValuesForYears(financialData.graphData, 'residualValueOfPlant'),
    // },
    {
      key: '2',
      name: t('result.finance.profitAndLoss.tables.rows.costOfPurchasedEnergy'),
      total:
        converToMillion(simulationData?.result.cogsWos) + ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'cogsWos'),
    },
    {
      key: '3',
      name: t(
        'result.finance.profitAndLoss.tables.rows.prePlantConsumptionBillingCost'
      ),
      total:
        converToMillion(simulationData?.result.preplantConsumptionBillingCost) +
        ` ${currencySymbol}`,
      ...getValuesForYears(
        financialData.graphData,
        'preplantConsumptionBillingCost'
      ),
    },
    {
      key: '13',
      name: t('result.finance.profitAndLoss.tables.rows.distributionCosts'),
      total:
        converToMillion(simulationData?.result.distributionCostWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'distributionCostWos'),
      children: [
        {
          key: '13-1',
          name: t(
            'result.finance.profitAndLoss.tables.rows.distributionCostOfPurchasedEnergyForConsumptionWos'
          ),
          total:
            converToMillion(
              simulationData?.result
                .distributionCostOfPurchasedEnergyForConsumptionWos
            ) + ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'distributionCostOfPurchasedEnergyForConsumptionWos'
          ),
        },
        {
          key: '13-2',
          name: t(
            'result.finance.profitAndLoss.tables.rows.prePlantDistributionCostOfPurchasedEnergy'
          ),
          total:
            converToMillion(
              simulationData?.result.preplantDistributionCostOfPurchasedEnergy
            ) + ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'preplantDistributionCostOfPurchasedEnergy'
          ),
        },
        {
          key: '13-3',
          name: t(
            'result.finance.profitAndLoss.tables.rows.distributionCostOfSoldEnergyDirectlyFromPlantWos'
          ),
          total:
            converToMillion(
              simulationData?.result
                .distributionCostOfSoldEnergyDirectlyFromPlantWos
            ) + ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'distributionCostOfSoldEnergyDirectlyFromPlantWos'
          ),
        },
      ],
    },
    {
      key: '4',
      name: t('result.finance.profitAndLoss.tables.rows.grossProfit'),
      total:
        converToMillion(simulationData?.result.grossProfitWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'grossProfitWos'),
    },
    {
      key: '5',
      name: t('result.finance.profitAndLoss.tables.rows.operatingExpenses'),
      total:
        converToMillion(simulationData?.result.totalOpexWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'totalOpexWos'),
      children: [
        {
          key: '5-4',
          name: t(
            'result.finance.profitAndLoss.tables.rows.powerPlantFixedOpex'
          ),
          total:
            converToMillion(simulationData?.result.powerPlantFixedOpex) +
            ` ${currencySymbol}`,
          ...getValuesForYears(financialData.graphData, 'powerPlantFixedOpex'),
        },
        {
          key: '5-5',
          name: t('result.finance.profitAndLoss.tables.rows.scenarioFixedOpex'),
          total:
            converToMillion(simulationData?.result.scenarioFixedOpex) +
            ` ${currencySymbol}`,
          ...getValuesForYears(financialData.graphData, 'scenarioFixedOpex'),
        },
      ],
    },
    {
      key: '7',
      name: t('result.finance.profitAndLoss.tables.rows.ebitda'),
      total:
        converToMillion(simulationData?.result.ebitdaWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'ebitdaWos'),
    },
    {
      key: '8',
      name: t('result.finance.profitAndLoss.tables.rows.plantDepreciation'),
      ...getValuesForYears(financialData.graphData, 'plantDepreciation'),
    },
    {
      key: '9',
      name: t('result.finance.profitAndLoss.tables.rows.ebit'),
      total:
        converToMillion(simulationData?.result.ebitWos) + ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'ebitWos'),
    },
    {
      key: '10',
      name: t(
        'result.finance.profitAndLoss.tables.rows.interestExpenseOfPlantCapex'
      ),
      total:
        converToMillion(simulationData?.result.plantLoanInterestPayment) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'plantLoanInterestPayment'),
    },
    {
      key: '11',
      name: t('result.finance.profitAndLoss.tables.rows.taxes'),
      total:
        converToMillion(simulationData?.result.incomeTaxWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'incomeTaxWos'),
    },
    {
      key: '12',
      name: t('result.finance.profitAndLoss.tables.rows.netIncome'),
      total:
        converToMillion(simulationData?.result.netIncomeWos) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'netIncomeWos'),
    },
  ];

  return (
    <StyledProfit>
      <Table
        dataSource={dataSource}
        columns={columns}
        expandable={{
          expandedRowKeys,
          onExpand,
          expandIcon: customExpandIcon,
          rowExpandable: (record) => !!record.children,
        }}
        pagination={false}
        scroll={{ x: scrollX }}
      />
    </StyledProfit>
  );
};

export default PNLWithoutStorageTable;
